import { useState, useEffect, useCallback, useRef } from 'react'
import { Box } from '@mantine/core'
import { Layout, DashboardHeader } from 'components'
import { arcHeroContentOffset } from 'components/ArcPageShell/constants'
import { CurrentUserContextProvider, useCurrentUser } from 'context'
import { useFeatureToggle, useMediaQuery, useMantineTheme } from 'hooks'
import { StudentDashboard } from './components/StudentDashboard'
import { StudentDashboardProvider } from './context/StudentDashboardContext'
import { StudentPendingScreen } from './components/StudentPendingScreen'
import { StudentDashBoardOnLoad } from './components/StudentDashboardSkeleton'
import { getDashboardStatus } from './service/studentDashboardService'
import type { StudentInvite } from 'api/arcMosaico/types'

const HEADER_HEIGHT = { mobile: 82, desktop: 138 }
const POLL_INTERVAL_MS = 15_000

function StudentDashboardContent() {
  const { currentUser } = useCurrentUser()
  const theme = useMantineTheme()
  const isMobile = useMediaQuery(`(max-width: ${theme.breakpoints.sm})`)
  const isArcEnabled = useFeatureToggle('arc-mosaico-project') as boolean

  const [dashboardLocked, setDashboardLocked] = useState<boolean | null>(null)
  const [invites, setInvites] = useState<StudentInvite[]>([])
  const [checking, setChecking] = useState(true)
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)

  const checkStatus = useCallback(async () => {
    try {
      const data = await getDashboardStatus()
      setDashboardLocked(data.dashboardLocked)
      setInvites(data.invites || [])
    } catch {
      setDashboardLocked(false)
    } finally {
      setChecking(false)
    }
  }, [])

  useEffect(() => {
    checkStatus()
  }, [checkStatus])

  // Show pending screen when dashboard is locked OR when all invites
  // have been declined (backend unlocks dashboard but student has no data).
  const allDeclined = invites.length > 0
    && invites.every(i => i.status === 'declined')

  const showPendingScreen = dashboardLocked || allDeclined

  // Poll while pending screen is shown so that
  // approval redirects to the dashboard automatically.
  useEffect(() => {
    if (showPendingScreen) {
      intervalRef.current = setInterval(checkStatus, POLL_INTERVAL_MS)
    }
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
        intervalRef.current = null
      }
    }
  }, [showPendingScreen, checkStatus])

  const handleInviteAdded = useCallback((invite: StudentInvite) => {
    setInvites(prev => [...prev, invite])
  }, [])

  if (checking) {
    return <StudentDashBoardOnLoad />
  }

  const heroClearance = isMobile ? HEADER_HEIGHT.mobile : HEADER_HEIGHT.desktop

  if (showPendingScreen) {
    return (
      <>
        <DashboardHeader name={currentUser?.full_name} isMobile={isMobile} />
        {/* Arc: hero clearance compensates the reduced container top padding (AR-578) */}
        <Box pt={isArcEnabled ? arcHeroContentOffset(heroClearance, isMobile) : heroClearance}>
          <StudentPendingScreen
            invites={invites}
            onInviteAdded={handleInviteAdded}
          />
        </Box>
      </>
    )
  }

  return (
    <StudentDashboardProvider>
      <StudentDashboard />
    </StudentDashboardProvider>
  )
}

export const StudentDashboardPage = () => {
  return (
    <CurrentUserContextProvider>
      <Layout>
        <StudentDashboardContent />
      </Layout>
    </CurrentUserContextProvider>
  )
}
